/**
 * API configuration for Form Engine Portal.
 * Reads documented `VITE_*` variables (see `.env.example`); other API settings use static defaults.
 */

const normalizeEnvValue = (value: string | undefined | null): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const FORM_DATA_SERVICE_API_VERSION = 'v1';
const CASE_MGMT_SERVICE_API_VERSION = 'v1';
const FORM_EXPORT_SERVICE_API_VERSION = 'v1';
const FORM_EXPORT_SERVICE_DOWNLOAD_PDF_TEMPLATE =
  'form-exports/download-pdf?id={0}&examId={1}&version={2}';

// ---------------------------------------------------------------------------
// Form Data Service API (submissions, definitions, workflow)
// Env: VITE_FORM_DATA_SERVICE_API_BASE_URL
// ---------------------------------------------------------------------------
const getFormDataServiceApiBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_FORM_DATA_SERVICE_API_BASE_URL);

  if (!configuredUrl && import.meta.env.DEV) {
    console.warn(
      '[FormDataServiceAPI] VITE_FORM_DATA_SERVICE_API_BASE_URL is not configured. Using default localhost URL.'
    );
  }

  return configuredUrl ?? 'https://localhost:7194';
};

export const FORM_DATA_SERVICE_API_CONFIG = {
  baseUrl: getFormDataServiceApiBaseUrl(),
  apiVersion: FORM_DATA_SERVICE_API_VERSION,
  timeout: 30000,
} as const;

const getFormDataServiceApiPath = (path: string): string => {
  return `/api/${FORM_DATA_SERVICE_API_CONFIG.apiVersion}${path}`;
};

export const FORM_DATA_SERVICE_API_ENDPOINTS = {
  formSubmissions: getFormDataServiceApiPath('/form-submissions'),
  formSubmissionClaim: () => getFormDataServiceApiPath('/form-submissions/claim'),
  formSubmissionsByExam: (examId: string) => getFormDataServiceApiPath(`/form-submissions/exam/${examId}`),
  formSubmissionById: (id: string) => getFormDataServiceApiPath(`/form-submissions/${id}`),
  formSubmissionByExamFormId: (examFormId: string) =>
    getFormDataServiceApiPath(`/form-submissions/exam-form/${examFormId}`),
  formSubmissionByExamAndFormId: (examId: string, examFormId: string) =>
    getFormDataServiceApiPath(
      `/form-submissions/by-exam-form/${encodeURIComponent(examId)}/${encodeURIComponent(examFormId)}`
    ),
  formSubmissionReset: getFormDataServiceApiPath('/form-submissions/reset'),
  formSubmissionSubmit: (id: string) =>
    getFormDataServiceApiPath(`/form-submissions/${encodeURIComponent(id)}/submit`),
  formSubmissionCreateAndSubmit: getFormDataServiceApiPath('/form-submissions/submit'),
  formDefinitionQuery: getFormDataServiceApiPath('/forms/definition/query'),
  formsByCategory: (category: string) =>
    getFormDataServiceApiPath(`/forms/by-category?formCategory=${encodeURIComponent(category)}`),
  formsAll: getFormDataServiceApiPath('/forms/all'),
  formsBySpecialties: getFormDataServiceApiPath('/forms/by-specialties'),
  formByCode: (formCode: string) => getFormDataServiceApiPath(`/forms/${encodeURIComponent(formCode)}`),
  workflowFormFieldMappings: (examFormId: number) =>
    getFormDataServiceApiPath(`/workflow/form-field-mappings?examFormId=${examFormId}`),
  workflowFormFieldMappingsByCode: (formCode: string, refresh?: boolean) =>
    getFormDataServiceApiPath(
      `/workflow/form-field-mappings-by-code?formCode=${encodeURIComponent(formCode)}${refresh ? '&refresh=true' : ''}`
    ),
} as const;

// ---------------------------------------------------------------------------
// Auth API
// Env: VITE_AUTH_SERVICE_URL, VITE_AUTH_API_VERSION, VITE_AUTH_PROVIDER_TYPE
// ---------------------------------------------------------------------------
const getProviderType = (): 'rest' | 'oauth2' | 'msal' => {
  const envValue = import.meta.env.VITE_AUTH_PROVIDER_TYPE;
  return (envValue as 'rest' | 'oauth2' | 'msal') ?? 'rest';
};

const getAuthApiBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_AUTH_SERVICE_URL);
  if (configuredUrl) {
    return configuredUrl;
  }
  if (import.meta.env.DEV) {
    console.warn('[AuthAPI] VITE_AUTH_SERVICE_URL is not set; using https://localhost:7043 for RBAC and auth.');
    return 'https://localhost:7043';
  }
  return 'https://auth-api.azure-api.net';
};

const authApiVersion = normalizeEnvValue(import.meta.env.VITE_AUTH_API_VERSION) ?? 'v1';

export const AUTH_API_CONFIG = {
  baseUrl: getAuthApiBaseUrl(),
  apiVersion: authApiVersion,
  timeout: 30000,
  providerType: getProviderType(),
} as const;

export const AUTH_API_ENDPOINTS = {
  login: `/api/${AUTH_API_CONFIG.apiVersion}/auth/login`,
  refresh: `/api/${AUTH_API_CONFIG.apiVersion}/auth/refresh`,
  logout: `/api/${AUTH_API_CONFIG.apiVersion}/auth/logout`,
  introspect: `/api/${AUTH_API_CONFIG.apiVersion}/auth/introspect`,
  register: `/api/${AUTH_API_CONFIG.apiVersion}/auth/register`,
  userProfile: `/api/${AUTH_API_CONFIG.apiVersion}/user/profile`,
  userClaims: `/api/${AUTH_API_CONFIG.apiVersion}/user/claims`,
  userVerify: `/api/${AUTH_API_CONFIG.apiVersion}/user/verify`,
  userEntra: `/api/${AUTH_API_CONFIG.apiVersion}/user/entra`,
  sessionCreate: `/api/${AUTH_API_CONFIG.apiVersion}/session/create`,
  sessionCurrent: `/api/${AUTH_API_CONFIG.apiVersion}/session/current`,
  sessionHeartbeat: `/api/${AUTH_API_CONFIG.apiVersion}/session/heartbeat`,
  sessionTerminate: `/api/${AUTH_API_CONFIG.apiVersion}/session/terminate`,
  sessionValidate: `/api/${AUTH_API_CONFIG.apiVersion}/session/validate`,
  sessionActive: `/api/${AUTH_API_CONFIG.apiVersion}/session/active`,
} as const;

// ---------------------------------------------------------------------------
// User API (profile, entra lookup, registration) — standard split for Form Engine Portal
// Env: VITE_API_USER_BASE_URL (NOT VITE_AUTH_SERVICE_URL)
// ---------------------------------------------------------------------------
const getUserApiBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_API_USER_BASE_URL);

  if (!configuredUrl && import.meta.env.DEV) {
    console.warn('[UserAPI] VITE_API_USER_BASE_URL is not configured.');
  }

  return configuredUrl ?? 'https://localhost:7006';
};

export const USER_API_CONFIG = {
  baseUrl: getUserApiBaseUrl(),
  apiVersion: normalizeEnvValue(import.meta.env.VITE_USER_API_VERSION) ?? 'v1',
  timeout: 30000,
  retryAttempts: 3,
  retryDelay: 1000,
  apiKey: import.meta.env.VITE_USER_API_KEY,
  apiKeyHeader: import.meta.env.VITE_USER_API_KEY_HEADER ?? 'X-API-Key',
} as const;

/** Service-to-service auth is not configured via env for this portal; extend here if needed. */
export const SERVICE_AUTH_CONFIG = {
  enabled: false,
  apiKey: undefined as string | undefined,
  apiKeyHeader: 'X-API-Key',
  serviceToken: undefined as string | undefined,
} as const;

// ---------------------------------------------------------------------------
// Case Management Service API
// Env: VITE_CASE_MGMT_SERVICE_API_BASE_URL
// ---------------------------------------------------------------------------
const getCaseMgmtServiceApiBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_CASE_MGMT_SERVICE_API_BASE_URL);

  if (!configuredUrl && import.meta.env.DEV) {
    console.warn('[CaseMgmtService] VITE_CASE_MGMT_SERVICE_API_BASE_URL is not configured.');
  }

  return configuredUrl ?? 'https://localhost:7093';
};

export const CASE_MGMT_SERVICE_API_CONFIG = {
  baseUrl: getCaseMgmtServiceApiBaseUrl(),
  apiVersion: CASE_MGMT_SERVICE_API_VERSION,
  timeout: 30000,
} as const;

// ---------------------------------------------------------------------------
// File Manager Service (diagnostic / medical record uploads, list, download)
// Env: VITE_FILE_MANAGER_API_BASE_URL
// ---------------------------------------------------------------------------
const getFileManagerApiBaseUrl = (): string => {
  const configuredUrl = normalizeEnvValue(import.meta.env.VITE_FILE_MANAGER_API_BASE_URL);

  if (!configuredUrl && import.meta.env.DEV) {
    console.warn('[FileManagerAPI] VITE_FILE_MANAGER_API_BASE_URL is not configured.');
  }

  return configuredUrl ?? 'https://localhost:7188';
};

export const FILE_MANAGER_API_CONFIG = {
  baseUrl: getFileManagerApiBaseUrl(),
  timeout: 60000,
} as const;

export const CASE_MGMT_SERVICE_API_ENDPOINTS = {
  examFormDetails: (examFormId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exam-forms/${examFormId}/details`,
  examFormHeader: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/form-header`,
  addFormToExam: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/forms`,
  examFormsByExamId: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/exam-forms`,
  deleteTriggeredFormsByExamId: (examId: string) =>
    `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/forms/triggered`,
  examWorkspace: (examId: string) => `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/workspace`,
  deleteExamForm: (examId: string, examFormId: string) =>
    `/api/${CASE_MGMT_SERVICE_API_CONFIG.apiVersion}/exams/${examId}/exam-forms/${examFormId}`,
  /**
   * Flowable-direct (no API version segment).
   * CaseMgmtService FlowableDirectController: GET /api/flowable/status?account=
   */
  flowableStatus: () => '/api/flowable/status',
  /**
   * Flowable-direct (no API version segment).
   * CaseMgmtService FlowableDirectController: POST /api/flowable/claim-task
   */
  flowableClaimTask: () => '/api/flowable/claim-task',
} as const;

// ---------------------------------------------------------------------------
// Form Export Service (PDF download URL builder)
// Env: VITE_FORM_EXPORT_API_BASE_URL
// ---------------------------------------------------------------------------
const getFormExportServiceApiBaseUrl = (): string => {
  const configured = normalizeEnvValue(import.meta.env.VITE_FORM_EXPORT_API_BASE_URL);
  const raw = configured ?? 'https://localhost:7166';
  const url = raw.replace(/\/api\/?$/i, '').replace(/\/+$/, '');
  return url || '';
};

const FORM_EXPORT_SERVICE_BASE_URL = getFormExportServiceApiBaseUrl();

export function buildFormExportPdfUrl(id: string, examId: string, version: string | undefined): string {
  const baseUrl = FORM_EXPORT_SERVICE_BASE_URL;
  const template = FORM_EXPORT_SERVICE_DOWNLOAD_PDF_TEMPLATE;
  if (!baseUrl) {
    console.error('[FormExportService] PDF URL not built: base URL is missing after normalization.');
    return '';
  }
  if (!template || !template.includes('{0}') || !template.includes('{1}') || !template.includes('{2}')) {
    console.error('[FormExportService] PDF URL not built: endpoint template must contain {0}, {1}, and {2}.');
    return '';
  }
  const path = template
    .replace('{0}', encodeURIComponent(id))
    .replace('{1}', encodeURIComponent(examId))
    .replace('{2}', encodeURIComponent(version ?? ''));
  return `${baseUrl}/api/${FORM_EXPORT_SERVICE_API_VERSION}/${path}`;
}

// ---------------------------------------------------------------------------
// Cross-portal URLs (host portal opens Form Engine Portal; Case Mgmt is sibling)
// Env: VITE_HOST_PORTAL_URL (preferred), VITE_HEALTHCARE_PORTAL_URL (legacy alias), VITE_CASE_MGMT_PORTAL_URL
// ---------------------------------------------------------------------------
const getHostPortalUrl = (): string =>
  normalizeEnvValue(import.meta.env.VITE_HOST_PORTAL_URL) ??
  normalizeEnvValue(import.meta.env.VITE_HEALTHCARE_PORTAL_URL) ??
  'http://localhost:5173';

/** Base URL of the host portal that embeds or opens Form Engine Portal (shared auth / postMessage). */
export const HOST_PORTAL_URL = getHostPortalUrl();

export const CASE_MGMT_PORTAL_URL =
  normalizeEnvValue(import.meta.env.VITE_CASE_MGMT_PORTAL_URL) ?? 'http://localhost:5174';
